import { useMemo, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  useMyWorkspaceMembership,
  usePendingInvitations,
  useRemoveMember,
  useRevokeInvitation,
  useUpdateMemberRole,
  useWorkspace,
  useWorkspaceMembers,
} from '@fluxter/shared';
import { Plus, Trash2, X } from 'lucide-react';
import { MemberAvatar } from '@/components/MemberAvatar';
import { InviteMemberModal } from '@/components/InviteMemberModal';
import { useConfirm } from '@/components/ConfirmDialog';
import { useSetTopBarSlots } from '@/components/TopBarSlots';
import { SkeletonList } from '@/components/Skeletons';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatRelativeTime } from '@/lib/relative-time';
import { cn } from '@/lib/utils';

interface MemberAttrs {
  name?: string;
  email?: string;
  role?: 'owner' | 'admin' | 'member';
  joined_at?: string | null;
}

interface MemberItem {
  id: string;
  attributes?: MemberAttrs;
}

interface InvitationAttrs {
  email?: string;
  role?: 'admin' | 'member';
  inviter_name?: string | null;
  inviter_email?: string | null;
  expires_at?: string | null;
  is_pending?: boolean;
}

interface InvitationItem {
  id: string;
  attributes?: InvitationAttrs;
}

const ROLE_LABEL: Record<NonNullable<MemberAttrs['role']>, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
};

/**
 * Role pill — same shape as the prototype's `.v1-tag` but tone'd
 * per role:
 *   owner  — primary navy tint
 *   admin  — brand-orange tint
 *   member — neutral surface-quiet
 */
const ROLE_BADGE_CLASS: Record<NonNullable<MemberAttrs['role']>, string> = {
  owner: 'bg-[oklch(0.55_0.18_245/0.14)] text-[oklch(0.4_0.14_245)] dark:text-[oklch(0.78_0.14_245)]',
  admin: 'bg-[oklch(0.74_0.16_50/0.14)] text-[oklch(0.45_0.16_50)] dark:text-[oklch(0.85_0.16_50)]',
  member: 'bg-[var(--surface-quiet)] text-foreground-muted',
};

/**
 * Glass redesign — Phase 7e.
 *
 * Workspace People page. Members + pending invitations as glass-card
 * row groups. Per Q5: glass-card row treatment instead of an
 * alternating-row table. Topbar contributions (Q7): breadcrumb in
 * left slot, "Invite member" CTA in cta slot (admin/owner only —
 * non-admins see no CTA).
 */
export const WorkspacePeoplePage = () => {
  const { workspaceSlug } = useParams({ from: '/w/$workspaceSlug/people' });
  const workspace = useWorkspace(workspaceSlug);
  const members = useWorkspaceMembers(workspaceSlug);
  const invitations = usePendingInvitations(workspaceSlug);
  const membership = useMyWorkspaceMembership(workspaceSlug);
  const updateRole = useUpdateMemberRole(workspaceSlug);
  const removeMember = useRemoveMember(workspaceSlug);
  const revokeInvitation = useRevokeInvitation(workspaceSlug);
  const { confirm, dialog } = useConfirm();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const workspaceName = (workspace.data as { data?: { attributes?: { name?: string } } } | undefined)
    ?.data?.attributes?.name;

  const memberList = (members.data as { data?: MemberItem[] } | undefined)?.data ?? [];
  const invitationList =
    (invitations.data as { data?: InvitationItem[] } | undefined)?.data ?? [];

  const myUserId = membership.userId;
  const isOwner = membership.isOwner;
  const isAdmin = membership.isAdmin;

  const handleRoleChange = async (
    targetUserId: string,
    nextRole: 'admin' | 'member',
  ): Promise<void> => {
    setError(null);
    try {
      await updateRole.mutateAsync({ userId: targetUserId, role: nextRole });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update role.');
    }
  };

  const handleRemove = async (target: MemberItem, isSelf: boolean): Promise<void> => {
    const name = target.attributes?.name ?? target.attributes?.email ?? 'this member';
    const ok = await confirm(
      isSelf
        ? {
            title: `Leave ${workspaceName ?? 'this workspace'}?`,
            description: "You'll lose access to all projects in this workspace.",
            confirmLabel: 'Leave workspace',
            destructive: true,
          }
        : {
            title: `Remove ${name}?`,
            description: 'They will lose access to this workspace immediately.',
            confirmLabel: 'Remove',
            destructive: true,
          },
    );
    if (!ok) return;

    setError(null);
    try {
      await removeMember.mutateAsync(target.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove member.');
    }
  };

  const handleRevoke = async (invitation: InvitationItem): Promise<void> => {
    const ok = await confirm({
      title: `Revoke invitation to ${invitation.attributes?.email ?? 'this email'}?`,
      description: 'The recipient will no longer be able to use the link in their email.',
      confirmLabel: 'Revoke',
      destructive: true,
    });
    if (!ok) return;
    setError(null);
    try {
      await revokeInvitation.mutateAsync(invitation.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not revoke invitation.');
    }
  };

  /* ── Topbar contributions ───────────────────────────────────────── */

  const topBarLeft = useMemo(
    () => (
      <nav
        aria-label="Breadcrumb"
        className="flex items-center gap-1.5 text-[13px]"
      >
        <span className="text-foreground-muted">{workspaceName ?? 'Workspace'}</span>
        <span aria-hidden="true" className="text-foreground-faint">
          /
        </span>
        <span className="text-foreground font-medium" aria-current="page">
          People
        </span>
      </nav>
    ),
    [workspaceName],
  );

  const topBarCta = useMemo(
    () =>
      isAdmin ? (
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          className={cn(
            'inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-lg border-0 px-2.5 text-[12.5px] font-medium text-white transition-shadow duration-100',
            'bg-[var(--primary)]',
            'shadow-[0_2px_8px_oklch(0.3_0.06_245/0.3)] hover:shadow-[0_3px_10px_oklch(0.3_0.06_245/0.4)]',
            'dark:shadow-[0_2px_12px_oklch(0.55_0.12_245/0.4)]',
          )}
        >
          <Plus className="size-3.5" aria-hidden="true" />
          Invite member
        </button>
      ) : null,
    [isAdmin],
  );

  useSetTopBarSlots({ left: topBarLeft, cta: topBarCta });

  return (
    <>
      <div className="space-y-6">
        <header>
          <h1 className="text-foreground text-[28px] font-semibold tracking-[-0.025em]">
            People
          </h1>
          <p className="text-foreground-faint mt-1 text-[12.5px]">
            Members of this workspace plus pending invitations.{' '}
            {!isAdmin
              ? "You can see who's here but can't change roles or invite new people."
              : null}
          </p>
        </header>

        {error ? (
          <p className="text-destructive text-[12.5px]" role="alert">
            {error}
          </p>
        ) : null}

        {/* ── Members ───────────────────────────────────────────── */}
        <section
          className={cn(
            'rounded-xl border backdrop-blur-md',
            'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] shadow-[var(--glass-card-shadow)]',
          )}
        >
          <header className="flex items-center justify-between border-b border-[var(--glass-card-border)] px-4 py-3">
            <h2 className="text-foreground text-[14px] font-semibold tracking-[-0.005em]">
              Members
              <span className="text-foreground-faint ml-1.5 text-[11.5px] font-normal tabular-nums">
                ({memberList.length})
              </span>
            </h2>
          </header>
          {members.isLoading ? (
            <SkeletonList rows={4} withAvatar className="rounded-none border-0" />
          ) : (
            <ul className="divide-y divide-[var(--glass-card-border)]">
              {memberList.map((m) => {
                const attrs = m.attributes ?? {};
                const role = attrs.role ?? 'member';
                const isSelf = m.id === myUserId;
                const isThisOwner = role === 'owner';
                const canChangeRole = isOwner && !isThisOwner;
                const canRemoveOther =
                  isAdmin && !isThisOwner && !isSelf && (role !== 'admin' || isOwner);
                const canSelfLeave = isSelf && !isThisOwner;

                return (
                  <li key={m.id} className="flex items-center gap-3 px-4 py-3">
                    <MemberAvatar
                      id={m.id}
                      name={attrs.name}
                      email={attrs.email}
                      size="sm"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-foreground truncate text-[13px] font-medium">
                        {attrs.name ?? attrs.email ?? '—'}
                        {isSelf ? (
                          <span className="text-foreground-faint ml-1.5 text-[11px] font-normal">
                            (you)
                          </span>
                        ) : null}
                      </p>
                      {attrs.name && attrs.email ? (
                        <p className="text-foreground-faint truncate text-[11.5px]">
                          {attrs.email}
                        </p>
                      ) : null}
                    </div>

                    {canChangeRole ? (
                      <Select
                        value={role}
                        onValueChange={(v) =>
                          void handleRoleChange(m.id, v as 'admin' | 'member')
                        }
                      >
                        <SelectTrigger size="sm" className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span
                        className={cn(
                          'rounded-full px-2.5 py-0.5 text-[11px] font-medium',
                          ROLE_BADGE_CLASS[role],
                        )}
                      >
                        {ROLE_LABEL[role]}
                      </span>
                    )}

                    {canRemoveOther || canSelfLeave ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => void handleRemove(m, isSelf)}
                        className="text-foreground-muted hover:text-destructive h-8 w-8 p-0"
                        aria-label={isSelf ? 'Leave workspace' : 'Remove member'}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    ) : (
                      <span className="w-8" aria-hidden="true" />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* ── Pending invitations (admin/owner only) ────────────── */}
        {isAdmin ? (
          <section
            className={cn(
              'rounded-xl border backdrop-blur-md',
              'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] shadow-[var(--glass-card-shadow)]',
            )}
          >
            <header className="flex items-center justify-between border-b border-[var(--glass-card-border)] px-4 py-3">
              <h2 className="text-foreground text-[14px] font-semibold tracking-[-0.005em]">
                Pending invitations
                <span className="text-foreground-faint ml-1.5 text-[11.5px] font-normal tabular-nums">
                  ({invitationList.length})
                </span>
              </h2>
            </header>
            {invitations.isLoading ? (
              <SkeletonList rows={2} withAvatar className="rounded-none border-0" />
            ) : invitationList.length === 0 ? (
              <p className="text-foreground-faint p-6 text-center text-[12.5px] italic">
                No pending invitations.
              </p>
            ) : (
              <ul className="divide-y divide-[var(--glass-card-border)]">
                {invitationList.map((inv) => {
                  const attrs = inv.attributes ?? {};
                  return (
                    <li key={inv.id} className="flex items-center gap-3 px-4 py-3">
                      <MemberAvatar id={inv.id} email={attrs.email} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="text-foreground truncate text-[13px] font-medium">
                          {attrs.email ?? '—'}
                        </p>
                        <p className="text-foreground-faint truncate text-[11.5px]">
                          Invited by {attrs.inviter_name ?? attrs.inviter_email ?? '—'}
                          {attrs.expires_at ? (
                            <> · expires {formatRelativeTime(attrs.expires_at)}</>
                          ) : null}
                        </p>
                      </div>
                      <span
                        className={cn(
                          'rounded-full px-2.5 py-0.5 text-[11px] font-medium',
                          ROLE_BADGE_CLASS[attrs.role ?? 'member'],
                        )}
                      >
                        {ROLE_LABEL[attrs.role ?? 'member']}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => void handleRevoke(inv)}
                        className="text-foreground-muted hover:text-destructive h-8 w-8 p-0"
                        aria-label="Revoke invitation"
                      >
                        <X className="size-4" aria-hidden="true" />
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        ) : null}
      </div>

      <InviteMemberModal
        workspaceSlug={workspaceSlug}
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        canInviteAdmin={isOwner}
      />
      {dialog}
    </>
  );
};
